import React from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import DevicesScreen from '../screens/DevicesScreen';
import DeviceDetailScreen from '../screens/DeviceDetailScreen';
import AlarmRulesScreen from '../screens/AlarmRulesScreen';
import EditDeviceScreen from '../screens/EditDeviceScreen';
import AddDeviceScreen from '../screens/AddDeviceScreen';
import AddDeviceWizardScreen from '../screens/AddDeviceWizardScreen';
import QRCodeScannerScreen from '../screens/QRCodeScannerScreen';
import ReportOptionsScreen from '../screens/ReportOptionsScreen';
import GalaxyHeaderBackground from '../components/GalaxyHeaderBackground';

export type DevicesStackParamList = {
  DevicesList:
    | {
        filter?: 'all' | 'online' | 'offline';
        refresh?: number;
      }
    | undefined;
  DeviceDetail: {
    deviceId: string;
    deviceName: string;
    deviceType?: string;
    hasBattery?: boolean;
  };
  AlarmRules: {
    deviceId: string;
    deviceName: string;
  };
  EditDevice: {
    deviceId: string;
    deviceName: string;
  };
  AddDevice: undefined;
  AddDeviceWizard: undefined;
  QRCodeScanner: {
    onScan?: (data: any) => void;
  };
  ReportOptions: {
    device: any;
    telemetryData: any[];
    organizationName: string;
  };
};

const Stack = createNativeStackNavigator<DevicesStackParamList>();

export default function DevicesStackNavigator() {
  const { t } = useTranslation();
  const { theme } = useTheme();

  return (
    <Stack.Navigator
      screenOptions={{
        headerBackground: () => <GalaxyHeaderBackground />,
        headerTintColor: '#ffffff',
        headerTitleStyle: {
          fontWeight: 'bold',
        },
        headerTransparent: true,
        headerShadowVisible: false,
      }}
    >
      <Stack.Screen
        name="DevicesList"
        component={DevicesScreen}
        options={{
          title: t('navigation.devices', 'Devices'),
        }}
      />
      <Stack.Screen
        name="DeviceDetail"
        component={DeviceDetailScreen}
        options={{
          title: t('navigation.deviceDetail', 'Device Details'),
        }}
      />
      <Stack.Screen
        name="AlarmRules"
        component={AlarmRulesScreen}
        options={{
          title: t('navigation.alarmRules', 'Alarm Rules'),
        }}
      />
      <Stack.Screen
        name="EditDevice"
        component={EditDeviceScreen}
        options={{
          title: t('navigation.editDevice', 'Edit Device'),
        }}
      />
      <Stack.Screen
        name="AddDevice"
        component={AddDeviceScreen}
        options={{
          title: t('navigation.addDevice', 'Add Device'),
        }}
      />
      <Stack.Screen
        name="AddDeviceWizard"
        component={AddDeviceWizardScreen}
        options={{
          title: t('navigation.addDeviceWizard', 'Device Setup Wizard'),
          headerShown: false, // Wizard has custom header
        }}
      />
      <Stack.Screen
        name="QRCodeScanner"
        component={QRCodeScannerScreen}
        options={{
          title: t('navigation.qrScanner', 'QR Scanner'),
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="ReportOptions"
        component={ReportOptionsScreen}
        options={{
          title: t('navigation.reportOptions', 'Report Options'),
        }}
      />
    </Stack.Navigator>
  );
}
